import { generateUniqueNumber } from './generate-unique-number';

export class WorkerBroker {
  private scheduledIntervalFunctions: Map<number, number | Function> = new Map([[0, () => {}]]);
  private unrespondedRequests: Map<number, { timerId: number; timerType: 'interval' }> = new Map();

  private workerUrl: string | undefined;

  private worker: Worker | undefined;

  constructor(workerUrl: string) {
    this.workerUrl = workerUrl;
  }

  initWorker(): void {
    if (typeof Worker !== 'undefined' && this.workerUrl) {

      this.worker = new Worker(this.workerUrl);

      this.worker.addEventListener('message', ({ data }) => {
        if (data.method === 'call') {
          const {
            params: { timerId, timerType }
          } = data;

          if (timerType === 'interval') {
            const idOrFunc = this.scheduledIntervalFunctions.get(timerId);

            if (typeof idOrFunc === 'number') {
              const timerIdAndTimerType = this.unrespondedRequests.get(idOrFunc);

              if (
                timerIdAndTimerType === undefined ||
                timerIdAndTimerType.timerId !== timerId ||
                timerIdAndTimerType.timerType !== timerType
              ) {
                throw new Error('The timer is in an undefined state.');
              }
            } else if (typeof idOrFunc !== 'undefined') {
              idOrFunc();
            } else {
              throw new Error('The timer is in an undefined state.');
            }
          }
        } else if (data.error === null && typeof data.id === 'number') {
          const { id } = data;

          const timerIdAndTimerType = this.unrespondedRequests.get(id);

          if (!timerIdAndTimerType) {
            throw new Error('The timer is in an undefined state.');
          }

          const { timerId, timerType } = timerIdAndTimerType;

          this.unrespondedRequests.delete(id);

          if (timerType === 'interval') {
            this.scheduledIntervalFunctions.delete(timerId);
          }
        } else {
          const {
            error: { message }
          } = data;

          throw new Error(message);
        }
      });
    } else {
      // Web workers are not supported in this environment.
      // You should add a fallback so that your program still executes correctly.
    }
  }

  clearInterval(timerId: number): void {
    if (!this.worker) {
      return;
    }

    const id = generateUniqueNumber(this.unrespondedRequests);

    this.unrespondedRequests.set(id, { timerId, timerType: 'interval' });
    this.scheduledIntervalFunctions.set(timerId, id);

    this.worker.postMessage({
      id,
      method: 'clear',
      params: { timerId, timerType: 'interval' }
    });
  }

  setInterval(func: () => unknown, delay?: number | undefined): number {
    if (!this.worker) {
      return -1;
    }

    const timerId = generateUniqueNumber(this.scheduledIntervalFunctions);

    this.scheduledIntervalFunctions.set(timerId, () => {
      func();

      if (typeof this.scheduledIntervalFunctions.get(timerId) === 'function') {
        if (!this.worker) {
          return;
        }

        this.worker.postMessage({
          id: null,
          method: 'set',
          params: {
            delay,
            now: performance.now(),
            timerId,
            timerType: 'interval'
          }
        });
      }
    });

    this.worker.postMessage({
      id: null,
      method: 'set',
      params: {
        delay,
        now: performance.now(),
        timerId,
        timerType: 'interval'
      }
    });

    return timerId;
  }

}
